import { FC, useEffect, useState } from 'react';

import { CheckCircleIcon, ExclamationIcon } from '@heroicons/react/outline';

import { PendingDuplicateSuspicionEntry } from '../utils/giftOrderCoupon.utils';
import { SeqChip } from './SeqChip';

interface SuspicionConfirmModalProps {
  open: boolean;
  /** 의심 마크 본체. seq, conflicts 가 메시지 표시에 사용된다. */
  pendingSuspicion: PendingDuplicateSuspicionEntry | null;
  /** confirm/unshare 등 백엔드 호출 진행 중 표시. */
  isSaving: boolean;
  /** "그분께 잘 보낸 거였어요" → confirm + 의심 제거 */
  onConfirmedSafe: () => void;
  /**
   * "중복이라 삭제했어요" → unshare + 의심 마크 정리.
   * 호출부는 모달을 닫지 않고, 성공(true) 시 resendAsk step 으로 전환한다.
   */
  onDelete: () => Promise<boolean>;
  /**
   * resendAsk 의 "지금 다시 보내기" → 같은 답례품 공유 시트 재오픈.
   * 이미 NotShared 상태이므로 첫 공유와 동일한 자연스러운 흐름.
   */
  onResend: () => void;
  /**
   * X / "닫기" / 백드롭.
   * - ask 단계: 마크 보존 → 다음 진입 시 재노출
   * - resendAsk 단계: 이미 unshare 끝 → 단순 닫기
   */
  onClose: () => void;
}

/**
 * 다건수신 의심 확인 모달.
 *
 * ShareConfirmModal 의 다건수신 안내에서 "확인하러 가기" 를 눌러 카톡방을 확인한
 * 사용자가 페이지로 복귀했을 때 자동 노출되는 모달. localStorage 의 의심 마크
 * (`PendingDuplicateSuspicionEntry`) 를 기준으로 동작한다.
 *
 * 2단계 step:
 *   ask: "확인하셨나요?"
 *     - 그분께 잘 보낸 거였어요 → onConfirmedSafe (confirm)
 *     - 중복이라 삭제했어요    → onDelete (unshare) → 성공 시 resendAsk step 진행
 *   resendAsk: (이미 NotShared) "다시 보내시겠어요?"
 *     - 다시 보내기 → onResend (공유 시트 재오픈)
 *     - 닫기       → onClose (단순 닫기)
 */
export const SuspicionConfirmModal: FC<SuspicionConfirmModalProps> = ({
  open,
  pendingSuspicion,
  isSaving,
  onConfirmedSafe,
  onDelete,
  onResend,
  onClose,
}) => {
  const [step, setStep] = useState<'ask' | 'resendAsk'>('ask');

  useEffect(() => {
    if (open) setStep('ask');
  }, [open, pendingSuspicion?.itemId]);

  if (!open || !pendingSuspicion) return null;

  const sortedConflicts = [...pendingSuspicion.conflicts].sort((a, b) => a.seq - b.seq);

  const handleBackdrop = () => {
    if (isSaving) return;
    onClose();
  };

  const handleDeleteClick = async () => {
    const ok = await onDelete();
    if (ok) setStep('resendAsk');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={handleBackdrop}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'ask' ? (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <ExclamationIcon className="h-6 w-6 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">카톡방 확인하셨나요?</h3>
              </div>
              <CloseButton disabled={isSaving} onClose={onClose} />
            </div>

            <div className="mb-4 break-keep text-sm leading-relaxed text-gray-700">
              {pendingSuspicion.seq != null && (
                <p className="mb-2 flex flex-wrap items-center gap-1.5">
                  <SeqChip seq={pendingSuspicion.seq} />
                  <span className="text-base font-bold text-gray-800">답례품</span>
                  <span>을</span>
                </p>
              )}
              {sortedConflicts.length > 0 && (
                <p className="flex flex-wrap items-center gap-1.5">
                  {sortedConflicts.map((c) => (
                    <SeqChip key={c.code} seq={c.seq} />
                  ))}
                  <span className="text-base font-bold text-gray-800">답례품</span>
                  <span>과 같은 분께 보내신 게 맞나요?</span>
                </p>
              )}
            </div>

            <p className="mb-5 break-keep text-xs leading-relaxed text-gray-500">
              결과를 알려주시면 그에 맞게 처리해 드릴게요.
            </p>

            <div className="flex flex-col gap-2">
              <button
                onClick={onConfirmedSafe}
                disabled={isSaving}
                className={`w-full rounded-xl border border-purple-200 bg-purple-50 py-3 text-sm font-bold text-purple-700 active:bg-purple-100 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '처리 중...' : '그분께 잘 보낸 거였어요'}
              </button>
              <button
                onClick={handleDeleteClick}
                disabled={isSaving}
                className={`w-full rounded-xl border border-red-200 bg-red-50 py-3 text-sm font-bold text-red-700 active:bg-red-100 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '처리 중...' : '중복이라 삭제했어요'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <CheckCircleIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  지금 바로 다시 보내시겠어요?
                </h3>
              </div>
              <CloseButton disabled={isSaving} onClose={onClose} />
            </div>

            <div className="mb-5 break-keep text-sm leading-relaxed text-gray-700">
              <p className="mb-2 flex flex-wrap items-center gap-1.5">
                {pendingSuspicion.seq != null && <SeqChip seq={pendingSuspicion.seq} />}
                <span className="text-base font-bold text-gray-800">답례품</span>
                <span>을 미전달 상태로 되돌렸어요.</span>
              </p>
              <p className="text-xs leading-relaxed text-gray-500">
                지금 바로 다른 분께 다시 보내시겠어요? 나중에 보내셔도 돼요.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
              >
                나중에
              </button>
              <button
                onClick={onResend}
                disabled={isSaving}
                className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                지금 다시 보내기
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CloseButton: FC<{ disabled: boolean; onClose: () => void }> = ({
  disabled,
  onClose,
}) => (
  <button
    onClick={onClose}
    disabled={disabled}
    className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  </button>
);
